// ---------------------------------------------------------------------------
// Client-side hash routing (§5) + URL preselection (§2.4).
//
//  - The confirmation page lives at `#/appointment-details` so a refresh no
//    longer 404s / errors on a server path — it's a hash, served by index.html.
//  - profile / status / region (and the `s` source tag) can be preselected via
//    URL params in either the search string or the hash query, e.g.
//    `/#/?profile=201&status=existing&region=1&s=newsletter`.
// ---------------------------------------------------------------------------

import type { AppConfig, PatientStatus, Profile, Region } from './types'
import type { BookingState, Step } from './booking/state'
import { isCompleteZip } from './validation'

export const CONFIRMATION_HASH = '#/appointment-details'
const CONFIRMATION_KEY = 'tpc:last-confirmation'

/** Merge params from the hash query (`#/?a=b`) and the search string. */
export function urlParams(): URLSearchParams {
  const merged = new URLSearchParams(window.location.search)
  const h = window.location.hash
  const qi = h.indexOf('?')
  if (qi >= 0) {
    new URLSearchParams(h.slice(qi + 1)).forEach((v, k) => merged.set(k, v))
  }
  return merged
}

function findProfile(config: AppConfig, id: number): Profile | null {
  return config.profiles.find((p) => p.id === id) ?? null
}

function findRegion(config: AppConfig, id: number): Region | null {
  for (const r of config.regions) {
    if (r.id === id) return r
    const leaf = r.sub_regions?.find((s) => s.id === id)
    if (leaf) return leaf
  }
  return null
}

/** Resolve profile/status/region preselections from the URL into a state patch. */
export function parsePreselect(config: AppConfig): Partial<BookingState> {
  const p = urlParams()
  const patch: Partial<BookingState> = {}

  const profileId = Number(p.get('profile'))
  if (Number.isFinite(profileId) && profileId) {
    const profile = findProfile(config, profileId)
    if (profile) patch.profile = profile
  }

  const status = p.get('status')
  if (status === 'new' || status === 'existing') patch.status = status as PatientStatus

  const regionId = Number(p.get('region'))
  if (Number.isFinite(regionId) && regionId) {
    const region = findRegion(config, regionId)
    if (region) patch.region = region
  }

  const zip = p.get('zip') || p.get('zip_code')
  if (zip && isCompleteZip(zip)) patch.zip = zip

  return patch
}

/** The hash a given step should show (confirmation is deep-linkable). */
export function hashForStep(step: Step): string {
  return step === 'confirmation' ? CONFIRMATION_HASH : '#/'
}

/** Reflect the current step in the URL hash without adding history noise. */
export function syncHash(step: Step): void {
  const next = hashForStep(step)
  const current = window.location.hash || '#/'
  // Preserve any preselect query already present on the base route.
  if (step !== 'confirmation' && current.startsWith('#/') && current !== CONFIRMATION_HASH) return
  if (current === next) return
  // Keep the search string in its proper place (before the hash), so the hash
  // itself stays an exact match for CONFIRMATION_HASH on restore.
  window.history.replaceState(null, '', window.location.pathname + window.location.search + next)
}

/** Persist a minimal confirmation snapshot so a refresh can restore it (§5.1). */
export function saveConfirmation(state: BookingState): void {
  try {
    const snapshot = {
      profile: state.profile,
      subProfile: state.subProfile,
      status: state.status,
      region: state.region,
      slot: state.slot,
      confirmationId: state.confirmationId
    }
    sessionStorage.setItem(CONFIRMATION_KEY, JSON.stringify(snapshot))
  } catch {
    /* storage unavailable (private mode) — refresh simply restarts the flow */
  }
}

/** Restore the confirmation snapshot if the user refreshed on that route. */
export function loadConfirmation(): BookingState | null {
  if (window.location.hash !== CONFIRMATION_HASH) return null
  try {
    const raw = sessionStorage.getItem(CONFIRMATION_KEY)
    if (!raw) return null
    const snap = JSON.parse(raw) as Partial<BookingState>
    if (!snap.confirmationId) return null
    return {
      step: 'confirmation',
      history: [],
      profile: snap.profile ?? null,
      subProfile: snap.subProfile ?? null,
      status: snap.status ?? null,
      region: snap.region ?? null,
      zip: null,
      coords: null,
      slotStart: null,
      slot: snap.slot ?? null,
      confirmationId: snap.confirmationId
    }
  } catch {
    return null
  }
}

export function clearConfirmation(): void {
  try {
    sessionStorage.removeItem(CONFIRMATION_KEY)
  } catch {
    /* ignore */
  }
}
